import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@app/shared/utils/common';

export const buttonVariants = cva('font-semibold', {
  variants: {
    variant: {
      accent:
        'w-full bg-accent text-accent-foreground shadow hover:bg-blue-600 disabled:bg-accent/70 disabled:text-accent-foreground/70',
      secondary: 'w-full text-accent rounded-full border border-accent',
      tertiary:
        'w-full bg-tertiary text-tertiary-foreground hover:bg-tertiary-foreground/40 hover:text-accent',
      text: 'text-accent hover:text-accent/90 hover:underline',
      ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
      danger: 'w-full bg-red-500 text-accent-foreground shadow hover:bg-red-600',
      custom: '',
    },
    size: {
      sm: 'text-sm h-8 rounded-lg',
      md: 'text-md h-12 rounded-lg',
    },
    shadow: {
      blue: 'shadow-blue',
      cyan: 'shadow-cyan',
    },
  },
  defaultVariants: {
    variant: 'accent',
    size: 'md',
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shadow, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, shadow, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button };
